import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformServer } from '@angular/common';

import { EMPTY, Observable, Subject, filter, share, take } from 'rxjs';

interface ObserverOptions {
  root: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
}

@Injectable({
  providedIn: 'root',
})
export class ScrolledPastService {
  private platform = inject(PLATFORM_ID);

  private observer: IntersectionObserver;
  private intersectionSubject = new Subject<IntersectionObserverEntry>();
  private intersectionSubject$ = this.intersectionSubject.asObservable().pipe(share());

  listenToElement(element: Element, offset?: number): Observable<IntersectionObserverEntry> {
    if (isPlatformServer(this.platform)) {
      return EMPTY;
    }

    if (!this.observer) {
      const options: ObserverOptions = {
        root: null,
      };
      if (offset) {
        options.rootMargin = `${offset}px`;
      }

      this.observer = new IntersectionObserver(this.loadingCallback.bind(this), options);

      // FixMe: polyfill hack to poll for intersection. Without this the observer listens to window scroll and resize events
      (this.observer as IntersectionObserver & { POLL_INTERVAL: number }).POLL_INTERVAL = 200;
    }

    this.observer.observe(element);

    return new Observable<IntersectionObserverEntry>((obs) => {
      this.intersectionSubject$
        .pipe(
          filter((entry) => entry.target === element),
          take(1)
        )
        .subscribe((entry) => {
          obs.next(entry);
          obs.complete();
          this.observer.unobserve(element);
        });
    });
  }

  private loadingCallback(entries: IntersectionObserverEntry[]) {
    entries.filter((entry) => entry.isIntersecting).forEach((entry) => this.intersectionSubject.next(entry));
  }
}
